@if (isLoading) {
  <div class="flex items-center justify-center min-h-[400px]">
    <div role="status">
      <svg aria-hidden="true"
           class="w-12 h-12 text-gray-200 animate-spin fill-blue-600"
           viewBox="0 0 100 101"
           fill="none"
           xmlns="http://www.w3.org/2000/svg">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"/>
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"/>
      </svg>
      <span class="sr-only">Loading...</span>
    </div>
  </div>
} @else if (realEstate) {
  <!-- Header Section with Gray Background -->
  <div class="bg-[#F2F2F7] border-b border-gray-100">
    <!-- Title Section -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 class="text-center text-gray-900 text-3xl md:text-4xl font-extrabold font-['Inter']">
        {{ realEstate?.real_estate_type }} - {{ realEstate?.neighborhood }} - {{ realEstate?.city }} - {{ realEstate?.federation_state }}
      </h1>
    </div>

    <!-- Main Content Section -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-10">

        <!-- Left Column: Carousel/Images -->
        <div class="lg:col-span-6">
          <div id="default-carousel" class="relative w-full mb-4" [attr.data-carousel]="(realEstate?.images && realEstate.images.length > 1) ? 'slide' : null">
            <div class="relative h-64 md:h-[450px] overflow-hidden rounded-sm">
              @if (realEstate?.images?.length === 1) {
                <img [src]="realEstate.images[0]" class="h-full w-full object-cover" alt="Real Estate Main Image">
              } @else {
                @for (image of realEstate?.images; track $index) {
                  <div class="hidden duration-700 ease-in-out" [attr.data-carousel-item]="true">
                    <img [src]="image" class="absolute block w-full h-full object-cover -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2" [alt]="'Real Estate Image ' + ($index + 1)">
                  </div>
                }
              }
            </div>
          </div>

          <!-- Thumbnails -->
          @if (realEstate?.images && realEstate.images.length > 1) {
            <div class="grid grid-cols-4 md:grid-cols-5 gap-2">
              @for (image of realEstate.images.slice(0, 5); track $index) {
                <div class="aspect-video rounded-md overflow-hidden cursor-pointer hover:opacity-80 transition">
                  <img [src]="image" class="w-full h-full object-cover" alt="Thumbnail">
                </div>
              }
            </div>
          }
        </div>

        <!-- Right Column: Info & Map -->
        <div class="lg:col-span-6">
          <!-- Tags -->
          @if (isLogged | async) {
          <div class="flex flex-wrap mb-4">
            @if (realEstate?.source) {
              <div class="px-3 py-0.5 m-1 bg-green-100 rounded-md inline-block">
                <p class="text-center justify-start text-green-800 text-sm font-medium font-['Inter'] leading-tight">
                  {{ realEstate.source }}
                </p>
              </div>
            }
            @if (realEstate?.occupied) {
              <div class="px-3 py-0.5 m-1 bg-green-100 rounded-md inline-block">
                <p class="text-center justify-start text-green-800 text-sm font-medium font-['Inter'] leading-tight">
                  Ocupado
                </p>
              </div>
            }
            @if (realEstate?.auction_type) {
              <div class="px-3 py-0.5 m-1 bg-green-100 rounded-md inline-block">
                <p class="text-center justify-start text-green-800 text-sm font-medium font-['Inter'] leading-tight">
                  {{ realEstate.auction_type }}
                </p>
              </div>
            }
            @if (realEstate?.sale_type) {
              <div class="px-3 py-0.5 m-1 bg-green-100 rounded-md inline-block">
                <p class="text-center justify-start text-green-800 text-sm font-medium font-['Inter'] leading-tight">
                  {{ realEstate.sale_type }}
                </p>
              </div>
            }
          </div>
          }

          <!-- Address & Meta -->
          <div class="flex items-center text-gray-500 text-sm mb-2">
            <svg class="w-4 h-4 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
            <span class="text-blue-600 truncate">{{ realEstate.address }}, {{
                realEstate?.city }} - {{ realEstate?.federation_state }}</span>
          </div>

          <!-- Map -->
          @if (realEstate.lat && realEstate.lng) {
            <div class="w-full h-92 rounded-sm overflow-hidden border border-gray-200 mb-6">
              <google-map [center]="{ lat: realEstate.lat, lng: realEstate.lng }" [zoom]="15" [options]="{ disableDefaultUI: false, mapTypeControl: false }" width="100%" height="100%">
                <map-marker [position]="{ lat: realEstate.lat, lng: realEstate.lng }" [options]="{ title: realEstate.city }"></map-marker>
              </google-map>
            </div>
          }

          <!-- Property Summary Badges -->
          <div class="border-b border-gray-100 pb-4">
            <p class="text-gray-900 font-normal mb-3">Sobre o Imóvel</p>
            <div class="flex flex-wrap gap-2">
              @if (realEstate?.real_estate_type) {
                <span
                  class="inline-flex items-center justify-center px-3 py-0.5 bg-white border border-gray-300 rounded-md text-gray-500 text-sm font-medium font-['Inter'] leading-tight">
                  {{ realEstate.real_estate_type }}
                </span>
              }
              @if (realEstate?.total_area) {
                <span
                  class="inline-flex items-center justify-center px-3 py-0.5 bg-white border border-gray-300 rounded-md text-gray-500 text-sm font-medium font-['Inter'] leading-tight">
                  {{ realEstate.total_area }}m²
                </span>
              }
              @if (realEstate?.parking_spaces) {
                <span
                  class="inline-flex items-center justify-center px-3 py-0.5 bg-white border border-gray-300 rounded-md text-gray-500 text-sm font-medium font-['Inter'] leading-tight">
                  {{ realEstate.parking_spaces }} Vagas
                </span>
              }
              @if (realEstate?.bedrooms) {
                <span
                  class="inline-flex items-center justify-center px-3 py-0.5 bg-white border border-gray-300 rounded-md text-gray-500 text-sm font-medium font-['Inter'] leading-tight">
                  {{ realEstate.bedrooms }} Quartos
                </span>
              }
              @if (realEstate?.suites) {
                <span
                  class="inline-flex items-center justify-center px-3 py-0.5 bg-white border border-gray-300 rounded-md text-gray-500 text-sm font-medium font-['Inter'] leading-tight">
                  {{ realEstate.suites }} Suítes
                </span>
              }
            </div>
          </div>

          <!-- Assessment Price Section -->
          <div class="mt-4 border-b border-gray-200 pb-4">
            @if (realEstate.assessment_price) {
              <div class="">
                <p class="text-gray-500 text-sm mb-1 ">Valor avaliado</p>
                <p class="text-indigo-600 text-2xl font-medium" [class.line-through]="isAssessmentPriceScratched()">{{ realEstate.assessment_price | currency: 'BRL' }}</p>
              </div>
            }
          </div>

          <!-- Current Auction Price Section -->
          <div class="mt-4 border-b border-gray-200 pb-4">
            <div class="flex items-center gap-3 mb-4">
              <span class="text-3xl font-extrabold text-gray-900">
                {{ getActiveAuctionData().price | currency: 'BRL' }}
              </span>
              @if (getActiveAuctionData().discount) {
                <span class="inline-flex items-center px-1 py-0.5 rounded-md text-center bg-green-100 text-green-800 text-sm font-medium font-['Inter'] leading-tight">
                  <svg class="w-4 h-4 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 14l-7 7m0 0l-7-7m7 7V3"/></svg>
                  {{ getActiveAuctionData().discount | number:'.0-0' }}%
                </span>
              }
            </div>

            <!-- Auction Details -->
            <div class="space-y-2 text-xs text-gray-400">
              @if (realEstate.auction_1_start_date) {
                <p [class.line-through]="checkPastDate(realEstate.auction_1_start_date)">
                  1º Leilão - Encerrado em {{ realEstate.auction_1_start_date | date:'dd/MM/yyyy às HH:mm':'UTC' }} -
                  Lance mínimo: {{ realEstate.auction_1_price | currency: 'BRL' }}
                </p>
              }
              @if (realEstate.auction_2_start_date) {
                <p [class.line-through]="checkPastDate(realEstate.auction_2_start_date)">
                  2º Leilão - Encerra em {{ realEstate.auction_2_start_date | date:'dd/MM/yyyy às HH:mm':'UTC' }} - Lance mínimo: {{ realEstate.auction_2_price | currency: 'BRL' }}
                </p>
              }
              @if (realEstate.minimum_selling_price && !realEstate.auction_1_start_date && !realEstate.auction_2_start_date) {
                <p>Lance mínimo: {{ realEstate.minimum_selling_price | currency: 'BRL' }}</p>
              }
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col sm:flex-row sm:items-center gap-2 mt-3 border-t border-gray-100">
            @if (isLogged | async) {
              <button type="button"
                      class="inline-flex justify-center items-center px-5 py-2.5 text-sm font-medium text-gray-900 bg-white rounded-md border border-gray-200 hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 transition shadow-sm" (click)="includeFavorite(realEstate.link)">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                Adicionar aos favoritos
              </button>
            } @else {
              <button type="button"
                 class="inline-flex justify-center items-center px-5 py-2.5 text-sm font-medium text-white bg-blue-700 rounded-md border border-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-200 transition shadow-sm"
                 (click)="openAuthChoiceModal()">
                Logar ou assinar agora
              </button>
            }
            @if (isLogged | async) {
              <a [href]="realEstate.link" target="_blank" rel="noopener noreferrer" class="inline-flex justify-center items-center px-2 py-2.5 text-sm font-medium text-blue-600 bg-transparent hover:text-blue-700 transition">
                Ver no site do leiloeiro
              </a>
            }
          </div>

        </div>
      </div>
    </div>
  </div>

  <!-- Bottom Details Section -->
  <div class="bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="grid grid-cols-1 gap-12">

        <!-- Tabs Side -->
        <div class="w-full">
          <div class="mb-8 lg:hidden">
            <label for="property-details-section" class="mb-2 block text-sm font-medium text-gray-900">Seção</label>
            <select
              id="property-details-section"
              class="block w-full rounded-md border border-gray-300 bg-white p-3 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
              [ngModel]="activeDetailsTab"
              (ngModelChange)="switchTab($event)">
              @for (tab of detailsTabs; track tab.id) {
                <option [ngValue]="tab.id">{{ tab.label }}</option>
              }
            </select>
          </div>

          <ul class="hidden lg:flex flex-wrap text-sm font-medium text-center border-b border-gray-200 mb-8" id="default-styled-tab" role="tablist">
            <li class="mr-2" role="presentation">
              <button class="inline-block p-4 border-b-2 rounded-t-lg transition active text-blue-600 border-blue-600" id="profile-styled-tab" type="button" role="tab" (click)="switchTab('official-auction-information')">Informações Oficiais do Leilão</button>
            </li>
            <li class="mr-2" role="presentation">
              <button class="inline-block p-4 border-b-2 rounded-t-lg border-transparent hover:text-gray-600 hover:border-gray-300 transition" id="settings-styled-tab" type="button" role="tab" (click)="switchTab('cost-analysis')">Simulação de Custos</button>
            </li>
            <li class="mr-2" role="presentation">
              <button class="inline-block p-4 border-b-2 rounded-t-lg border-transparent hover:text-gray-600 hover:border-gray-300 transition" id="neighborhood-styled-tab" type="button" role="tab" (click)="switchTab('neighborhood-analysis')">Análise do Entorno</button>
            </li>
            <li class="mr-2" role="presentation">
              <button class="inline-block p-4 border-b-2 rounded-t-lg border-transparent hover:text-gray-600 hover:border-gray-300 transition" id="validation-risks-styled-tab" type="button" role="tab" (click)="switchTab('validation-risks')">Validação e riscos</button>
            </li>
          </ul>

          <div id="default-styled-tab-content">
            <div id="official-auction-information" role="tabpanel">
              @if (isLogged | async) {
                <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <!-- Text and Documents -->
                <div class="lg:col-span-7">
                  <div class="prose prose-sm max-w-none text-gray-600 leading-relaxed mb-8">
                    <p class="font-bold">Os documentos listados abaixo estão disponíveis no site oficial do leiloeiro. Ao clicar no link, você será direcionado ao download direto dos arquivos.</p>
                  </div>

                  <div class="space-y-3">
                    @if (realEstate?.documents) {
                      @for (file of realEstate.documents | keyvalue; track file.key) {
                        <a [href]="file.value" target="_blank" class="flex items-center text-blue-600 hover:underline font-medium">
                          <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM6 20V4h7v5h5v11H6z"/></svg>
                          {{ file.key.charAt(0).toUpperCase() + file.key.slice(1).toLowerCase() }}
                        </a>
                      }
                    } @else {
                      <p class="text-sm text-gray-400 italic">Nenhum documento disponível.</p>
                    }
                  </div>
                </div>

                <!-- Info Cards -->
                <div class="lg:col-span-5 space-y-6">
                  <!-- Sobre o Leilão -->
                  @if (realEstate.auctioneer || realEstate.auction_type || realEstate.sale_type) {
                    <div class="bg-gray-50 p-6 rounded-xl border border-gray-100">
                      <h4 class="font-bold text-gray-900 mb-4">Sobre o leilão</h4>
                      <div class="space-y-3 text-sm">
                        @if (realEstate.auctioneer) {
                          <div class="flex justify-between py-2 border-b border-gray-200">
                            <span class="text-gray-500">Comitente</span>
                            <span class="text-gray-900 font-medium">{{ realEstate.auctioneer }}</span>
                          </div>
                        }
                        @if (realEstate.auctioneer) {
                          <div class="flex justify-between py-2 border-b border-gray-200">
                            <span class="text-gray-500">Leiloeiro</span>
                            <span class="text-gray-900 font-medium">{{ realEstate.auctioneer }}</span>
                          </div>
                        }
                        @if (realEstate.auction_type) {
                          <div class="flex justify-between py-2 border-b border-gray-200">
                            <span class="text-gray-500">Tipo de leilão</span>
                            <span class="text-gray-900 font-medium">{{ realEstate.auction_type }}</span>
                          </div>
                        }
                        @if (realEstate.sale_type) {
                          <div class="flex justify-between py-2">
                            <span class="text-gray-500">Modalidade</span>
                            <span class="text-gray-900 font-medium">{{ realEstate.sale_type }}</span>
                          </div>
                        }
                      </div>
                    </div>
                  }

                  <!-- Preço e fases -->
                  <div class="bg-gray-50 p-6 rounded-xl border border-gray-100">
                    <h4 class="font-bold text-gray-900 mb-4">Preço e fases do leilão</h4>
                    <div class="space-y-3 text-sm">
                      <div class="flex justify-between py-2 border-b border-gray-200">
                        <span class="text-gray-500">Avaliação do leiloeiro</span>
                        <span class="text-gray-900 font-medium" [class.line-through]="isAssessmentPriceScratched()">{{ realEstate.assessment_price | currency: 'BRL' }}</span>
                      </div>
                      @if (realEstate.auction_1_price) {
                        <div class="flex justify-between py-2 border-b border-gray-200">
                          <span class="text-gray-500">1º Leilão</span>
                          <span class="text-gray-900 font-medium">{{ realEstate.auction_1_price | currency: 'BRL' }}</span>
                        </div>
                      }
                      @if (realEstate.auction_2_price) {
                        <div class="flex justify-between py-2 border-b border-gray-200">
                          <span class="text-gray-500">2º Leilão</span>
                          <span class="text-gray-900 font-medium">{{ realEstate.auction_2_price | currency: 'BRL' }}</span>
                        </div>
                      }
                      @if (realEstate.minimum_selling_price && !realEstate.auction_1_price && !realEstate.auction_2_price) {
                        <div class="flex justify-between py-2 border-b border-gray-200">
                          <span class="text-gray-500">Lance mínimo</span>
                          <span class="text-gray-900 font-medium">{{ realEstate.minimum_selling_price | currency: 'BRL' }}</span>
                        </div>
                      }
                    </div>
                  </div>

                  <!-- Condições de pagamento -->
                  @if (realEstate.payment_conditions) {
                    <div class="bg-gray-50 p-6 rounded-xl border border-gray-100">
                      <h4 class="font-bold text-gray-900 mb-4">Condições de pagamento</h4>
                      <div class="text-sm text-gray-600 space-y-3">
                        <p>{{ realEstate.payment_conditions }}</p>
                      </div>
                    </div>
                  }
                </div>
              </div>
              } @else {
                <ng-container *ngTemplateOutlet="guestTabAccessTemplate"></ng-container>
              }
            </div>

            <div id="cost-analysis" class="hidden" role="tabpanel">
              @if (isLogged | async) {
                @if (costReport) {
                  <app-financial-report [costReport]="costReport"></app-financial-report>
                }
              } @else {
                <ng-container *ngTemplateOutlet="guestTabAccessTemplate"></ng-container>
              }
            </div>

            <div id="neighborhood-analysis" class="hidden" role="tabpanel">
              @if (isLogged | async) {

<!--              <div class="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">-->
<!--                <div class="xl:col-span-5">-->
<!--                  <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-3">Análise do entorno</p>-->
<!--                  <p class="text-gray-600 leading-relaxed mb-8 max-w-2xl">-->
<!--                    Uma visão estimada do entorno para apoiar sua análise inicial sobre mobilidade,-->
<!--                    serviços, conveniência e potencial da região.-->
<!--                  </p>-->

<!--                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">-->
<!--                    <div class="bg-gray-50 border border-gray-100 rounded-xl p-5 transition hover:-translate-y-0.5 hover:border-blue-100 hover:bg-white hover:shadow-sm">-->
<!--                      <div class="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-4">-->
<!--                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">-->
<!--                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h8m-8 5h8m-6 5h4M6 3h12a2 2 0 012 2v13a3 3 0 01-3 3H7a3 3 0 01-3-3V5a2 2 0 012-2z"/>-->
<!--                        </svg>-->
<!--                      </div>-->
<!--                      <h4 class="text-gray-900 font-bold mb-2">Mobilidade</h4>-->
<!--                      <p class="text-sm text-gray-600">Acessos, transporte público e deslocamento diário.</p>-->
<!--                    </div>-->

<!--                    <div class="bg-gray-50 border border-gray-100 rounded-xl p-5 transition hover:-translate-y-0.5 hover:border-indigo-100 hover:bg-white hover:shadow-sm">-->
<!--                      <div class="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4">-->
<!--                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">-->
<!--                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3M9 9h1m-1 4h1m4-4h1m-1 4h1"/>-->
<!--                        </svg>-->
<!--                      </div>-->
<!--                      <h4 class="text-gray-900 font-bold mb-2">Serviços</h4>-->
<!--                      <p class="text-sm text-gray-600">Comércio, saúde, educação e rotina no entorno.</p>-->
<!--                    </div>-->

<!--                    <div class="bg-gray-50 border border-gray-100 rounded-xl p-5 transition hover:-translate-y-0.5 hover:border-emerald-100 hover:bg-white hover:shadow-sm">-->
<!--                      <div class="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">-->
<!--                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">-->
<!--                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 1.12-3 2.5S10.343 13 12 13s3 1.12 3 2.5S13.657 18 12 18m0-10V6m0 12v-2m8-4a8 8 0 11-16 0 8 8 0 0116 0z"/>-->
<!--                        </svg>-->
<!--                      </div>-->
<!--                      <h4 class="text-gray-900 font-bold mb-2">Liquidez</h4>-->
<!--                      <p class="text-sm text-gray-600">Indícios de demanda para revenda ou locação.</p>-->
<!--                    </div>-->

<!--                    <div class="bg-stone-50 border border-stone-100 rounded-xl p-5 transition hover:-translate-y-0.5 hover:border-amber-100 hover:bg-white hover:shadow-sm">-->
<!--                      <div class="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-4">-->
<!--                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">-->
<!--                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>-->
<!--                        </svg>-->
<!--                      </div>-->
<!--                      <h4 class="text-gray-900 font-bold mb-2">Atenção</h4>-->
<!--                      <p class="text-sm text-gray-600">Pontos que merecem validação presencial.</p>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                </div>-->

<!--                <div class="xl:col-span-7">-->
<!--                  <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">-->
<!--                    @if (realEstate.lat && realEstate.lng) {-->
<!--                      <div class="h-72 sm:h-80 md:h-[420px]">-->
<!--                        <google-map #neighborhoodMap [center]="{ lat: realEstate.lat, lng: realEstate.lng }" [zoom]="15" [options]="{ disableDefaultUI: false, mapTypeControl: false, streetViewControl: false }" width="100%" height="100%">-->
<!--                          <map-marker [position]="{ lat: realEstate.lat, lng: realEstate.lng }" [options]="{ title: realEstate.address }"></map-marker>-->
<!--                          @for (point of analysisMapPoints; track point.title) {-->
<!--                            <map-marker-->
<!--                              [position]="getAnalysisPointPosition(point)"-->
<!--                              [options]="{ title: point.title, label: getAnalysisPointLabel(point) }">-->
<!--                            </map-marker>-->
<!--                          }-->
<!--                        </google-map>-->
<!--                      </div>-->
<!--                    } @else {-->
<!--                      <div class="h-72 sm:h-80 md:h-[420px] bg-gray-50 flex items-center justify-center px-6 text-center">-->
<!--                        <p class="text-gray-500">Mapa indisponível para este imóvel.</p>-->
<!--                      </div>-->
<!--                    }-->

<!--                    <div class="p-5 sm:p-6">-->
<!--                      <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">-->
<!--                        <div>-->
<!--                          <h4 class="text-gray-900 text-lg sm:text-xl font-bold mb-1">Resumo estimado da localização</h4>-->
<!--                          <p class="text-sm text-gray-500">{{ realEstate.address }}, {{ realEstate.city }} - {{ realEstate.federation_state }}</p>-->
<!--                          <p class="text-xs text-gray-400 mt-1">Dados aproximados, baseados em fontes públicas e análise automatizada.</p>-->
<!--                        </div>-->
<!--                        <div class="text-left md:text-right">-->
<!--                          <p class="text-2xl sm:text-[1.7rem] font-semibold text-gray-800 leading-tight">8.1<span class="text-sm text-gray-400 font-medium">/10</span></p>-->
<!--                          <p class="text-xs text-gray-500">indicador estimado</p>-->
<!--                        </div>-->
<!--                      </div>-->

<!--                      <div class="grid grid-cols-10 gap-1.5 mb-6" aria-label="Indicador regional estimado">-->
<!--                        <div class="h-2 rounded-full bg-blue-600"></div>-->
<!--                        <div class="h-2 rounded-full bg-blue-600"></div>-->
<!--                        <div class="h-2 rounded-full bg-blue-600"></div>-->
<!--                        <div class="h-2 rounded-full bg-blue-500"></div>-->
<!--                        <div class="h-2 rounded-full bg-blue-500"></div>-->
<!--                        <div class="h-2 rounded-full bg-blue-400"></div>-->
<!--                        <div class="h-2 rounded-full bg-blue-400"></div>-->
<!--                        <div class="h-2 rounded-full bg-blue-300"></div>-->
<!--                        <div class="h-2 rounded-full bg-gray-200"></div>-->
<!--                        <div class="h-2 rounded-full bg-gray-200"></div>-->
<!--                      </div>-->

<!--                      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">-->
<!--                        <div class="border border-gray-100 rounded-lg px-4 py-3 transition hover:border-blue-100 hover:bg-gray-50">-->
<!--                          <p class="text-xs text-gray-500 mb-1">Perfil</p>-->
<!--                          <p class="text-sm text-gray-900 font-semibold">Urbano consolidado</p>-->
<!--                        </div>-->
<!--                        <div class="border border-gray-100 rounded-lg px-4 py-3 transition hover:border-blue-100 hover:bg-gray-50">-->
<!--                          <p class="text-xs text-gray-500 mb-1">Uso provável</p>-->
<!--                          <p class="text-sm text-gray-900 font-semibold">Moradia ou renda</p>-->
<!--                        </div>-->
<!--                        <div class="border border-gray-100 rounded-lg px-4 py-3 transition hover:border-blue-100 hover:bg-gray-50">-->
<!--                          <p class="text-xs text-gray-500 mb-1">Validação</p>-->
<!--                          <p class="text-sm text-gray-900 font-semibold">Validação presencial recomendada</p>-->
<!--                        </div>-->
<!--                      </div>-->

<!--                      <div class="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-4">-->
<!--                        <svg class="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">-->
<!--                          <path d="M11 17h2v-6h-2v6zm1-14C6.48 3 2 7.48 2 13s4.48 10 10 10 10-4.48 10-10S17.52 3 12 3zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-12h2V7h-2v2z"/>-->
<!--                        </svg>-->
<!--                        <p>-->
<!--                          Este resumo reúne sinais estimados da localização para apoiar sua análise inicial.-->
<!--                          Antes de dar um lance, confirme presencialmente as condições da rua, acesso,-->
<!--                          ruído, segurança, conservação e demanda local.-->
<!--                        </p>-->
<!--                      </div>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                </div>-->
<!--              </div>-->

<!--              <div class="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">-->
<!--                <div class="lg:col-span-7 bg-white border border-gray-200 rounded-xl shadow-sm p-5 sm:p-6">-->
<!--                  <div class="mb-5">-->
<!--                    <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Mobilidade e acesso</p>-->
<!--                    <h4 class="text-gray-900 text-lg sm:text-xl font-bold mb-2">Sinais de deslocamento para validar</h4>-->
<!--                    <p class="text-sm text-gray-600 leading-relaxed">-->
<!--                      Uma leitura indicativa sobre conexões e deslocamentos que devem ser confirmados-->
<!--                      presencialmente e em horários diferentes.-->
<!--                    </p>-->
<!--                  </div>-->

<!--                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">-->
<!--                    @for (item of mobilityInsights; track item.label) {-->
<!--                      <div class="rounded-lg border border-gray-100 bg-gray-50 p-4 transition hover:bg-white hover:border-blue-100 hover:shadow-sm">-->
<!--                        <p class="text-xs text-gray-500 mb-1">{{ item.label }}</p>-->
<!--                        <p class="text-sm font-semibold text-gray-900 mb-2">{{ item.value }}</p>-->
<!--                        <p class="text-xs text-gray-600 leading-relaxed">{{ item.detail }}</p>-->
<!--                      </div>-->
<!--                    }-->
<!--                  </div>-->
<!--                </div>-->

<!--                <div class="lg:col-span-5 bg-white border border-gray-200 rounded-xl shadow-sm p-5 sm:p-6">-->
<!--                  <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Perfil da região</p>-->
<!--                  <h4 class="text-gray-900 text-lg sm:text-xl font-bold mb-4">Contexto urbano estimado</h4>-->

<!--                  <div class="space-y-3">-->
<!--                    <div class="flex items-start justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50 p-4">-->
<!--                      <div>-->
<!--                        <p class="text-xs text-gray-500 mb-1">Perfil predominante</p>-->
<!--                        <p class="text-sm font-semibold text-gray-900">Urbano consolidado</p>-->
<!--                      </div>-->
<!--                      <span class="text-xs text-gray-500">indicativo</span>-->
<!--                    </div>-->
<!--                    <div class="flex items-start justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50 p-4">-->
<!--                      <div>-->
<!--                        <p class="text-xs text-gray-500 mb-1">Uso observado</p>-->
<!--                        <p class="text-sm font-semibold text-gray-900">Residencial com serviços próximos</p>-->
<!--                      </div>-->
<!--                      <span class="text-xs text-gray-500">aprox.</span>-->
<!--                    </div>-->
<!--                    <div class="flex items-start justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50 p-4">-->
<!--                      <div>-->
<!--                        <p class="text-xs text-gray-500 mb-1">Leitura recomendada</p>-->
<!--                        <p class="text-sm font-semibold text-gray-900">Avaliar rua, quadra e rotina local</p>-->
<!--                      </div>-->
<!--                      <span class="text-xs text-gray-500">presencial</span>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                </div>-->
<!--              </div>-->

<!--              <div class="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">-->
<!--                <div class="lg:col-span-4 bg-stone-50 border border-stone-100 rounded-xl p-5 sm:p-6">-->
<!--                  <p class="text-amber-700 text-sm font-semibold uppercase tracking-wide mb-2">Pontos de atenção</p>-->
<!--                  <h4 class="text-gray-900 text-lg font-bold mb-4">Checklist para diligência</h4>-->
<!--                  <div class="space-y-2">-->
<!--                    @for (check of attentionChecks; track check) {-->
<!--                      <div class="flex items-start gap-2 text-sm text-gray-700">-->
<!--                        <span class="mt-2 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>-->
<!--                        <span>{{ check }}</span>-->
<!--                      </div>-->
<!--                    }-->
<!--                  </div>-->
<!--                </div>-->

<!--                <div class="lg:col-span-4 bg-white border border-gray-200 rounded-xl shadow-sm p-5 sm:p-6">-->
<!--                  <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Comparáveis próximos</p>-->
<!--                  <h4 class="text-gray-900 text-lg font-bold mb-4">Referências para análise</h4>-->
<!--                  <div class="space-y-3">-->
<!--                    @for (comparable of nearbyComparableSignals; track comparable.type) {-->
<!--                      <div class="rounded-lg border border-gray-100 bg-gray-50 p-4">-->
<!--                        <p class="text-sm font-semibold text-gray-900">{{ comparable.type }}</p>-->
<!--                        <p class="text-xs text-gray-500 mt-1">{{ comparable.range }}</p>-->
<!--                        <p class="text-xs text-gray-600 mt-2 leading-relaxed">{{ comparable.note }}</p>-->
<!--                      </div>-->
<!--                    }-->
<!--                  </div>-->
<!--                </div>-->

<!--                <div class="lg:col-span-4 bg-white border border-gray-200 rounded-xl shadow-sm p-5 sm:p-6">-->
<!--                  <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Liquidez estimada</p>-->
<!--                  <h4 class="text-gray-900 text-lg font-bold mb-4">Sinais de demanda</h4>-->
<!--                  <div class="space-y-3">-->
<!--                    @for (signal of demandSignals; track signal.label) {-->
<!--                      <div class="rounded-lg border border-gray-100 bg-gray-50 p-4">-->
<!--                        <p class="text-sm font-semibold text-gray-900">{{ signal.label }}</p>-->
<!--                        <p class="text-xs text-gray-600 mt-2 leading-relaxed">{{ signal.description }}</p>-->
<!--                      </div>-->
<!--                    }-->
<!--                  </div>-->
<!--                </div>-->
<!--              </div>-->

              <app-real-estate-nearby-places [realEstate]="realEstate"></app-real-estate-nearby-places>
              } @else {
                <ng-container *ngTemplateOutlet="guestTabAccessTemplate"></ng-container>
              }
            </div>

            <div id="validation-risks" class="hidden" role="tabpanel">
              @if (isLogged | async) {
                <div>
                <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-3">Validação e riscos</p>
                <h3 class="text-gray-900 text-2xl md:text-3xl font-extrabold font-['Inter'] leading-tight">
                  Pontos importantes antes do lance
                </h3>
                <p class="mt-4 text-gray-600 text-base font-normal font-['Inter'] leading-relaxed max-w-3xl">
                  Veja sinais estimados, verificações recomendadas e fatores que merecem atenção antes de participar do leilão.
                </p>
                <p class="mt-4 text-sm text-gray-500 leading-relaxed max-w-4xl">
                  Esta análise é gerada com base nas informações disponíveis sobre o imóvel e tem caráter estimativo, servindo apenas como apoio à avaliação inicial e identificação de possíveis pontos de atenção.
                </p>
              </div>

              <section class="mt-8">
                <div class="mb-5">
                  <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Radar de atenção</p>
                  <h4 class="text-gray-900 text-2xl font-extrabold mb-2">Sinais indicativos para avaliar</h4>
                  <p class="text-gray-600 leading-relaxed max-w-3xl">
                    Os status abaixo são estimados e ajudam a organizar pontos que merecem validação documental, operacional ou presencial.
                  </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  @for (indicator of riskIndicators; track indicator.title) {
                    <article class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-100 hover:shadow-md">
                      <div class="mb-4 flex items-start justify-between gap-4">
                        <div class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-slate-50 text-slate-700">
                          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3l7 4v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V7l7-4z"/>
                          </svg>
                        </div>
                        <span class="rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
                          {{ indicator.status }}
                        </span>
                      </div>
                      <h5 class="text-gray-900 text-lg font-bold mb-2">{{ indicator.title }}</h5>
                      <p class="text-sm text-gray-600 leading-relaxed">{{ indicator.description }}</p>
                      @if (indicator.recommendation) {
                        <p class="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 leading-relaxed">
                          {{ indicator.recommendation }}
                        </p>
                      }
                    </article>
                  }
                </div>
              </section>

              <section class="mt-10 grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                <div class="xl:col-span-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-6">
                  <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Checklist de validação</p>
                  <h4 class="text-gray-900 text-2xl font-extrabold mb-2">Checklist antes do lance</h4>
                  <p class="text-gray-600 text-sm leading-relaxed">
                    Organize os principais pontos recomendados para análise antes de participar do leilão.
                  </p>

                  <div class="mt-6">
                    <div class="mb-2 flex items-center justify-between text-sm">
                      <span class="font-medium text-gray-700">{{ getValidationChecklistCompletedCount() }} de {{ validationChecklist.length }} itens</span>
                      <span class="font-semibold text-gray-900">{{ getValidationChecklistProgress() }}%</span>
                    </div>
                    <div class="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div class="h-full rounded-full bg-blue-600 transition-all duration-300" [style.width.%]="getValidationChecklistProgress()"></div>
                    </div>
                    @if (isValidationChecklistComplete()) {
                      <p class="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                        Checklist concluído
                      </p>
                    }
                  </div>
                </div>

                <div class="xl:col-span-7 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-6">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    @for (item of validationChecklist; track item.id) {
                      <label class="group flex cursor-pointer items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 transition hover:border-blue-100 hover:bg-white">
                        <input
                          type="checkbox"
                          class="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          [checked]="isValidationChecklistItemChecked(item.id)"
                          (change)="toggleValidationChecklistItem(item.id, $event)">
                        <span class="text-sm font-medium text-gray-800 leading-relaxed group-hover:text-gray-900">{{ item.label }}</span>
                      </label>
                    }
                  </div>
                </div>
              </section>

              <section class="mt-10">
                <div class="mb-5">
                  <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Custos adicionais</p>
                  <h4 class="text-gray-900 text-2xl font-extrabold mb-2">Custos frequentemente esquecidos</h4>
                  <p class="text-gray-600 leading-relaxed max-w-3xl">
                    Alguns custos podem não estar incluídos no valor do lance e devem ser considerados na análise.
                  </p>
                </div>

                <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  @for (cost of estimatedCosts; track cost.title) {
                    <div class="flex flex-col gap-4 border-b border-gray-100 p-5 last:border-b-0 sm:flex-row sm:items-start sm:gap-5">
                      <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-700">
                        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 14l2 2 4-4M7 4h10a2 2 0 012 2v14l-4-2-3 2-3-2-4 2V6a2 2 0 012-2z"/>
                        </svg>
                      </div>
                      <div>
                        <h5 class="text-gray-900 font-bold mb-1">{{ cost.title }}</h5>
                        <p class="text-sm text-gray-600 leading-relaxed">{{ cost.description }}</p>
                      </div>
                    </div>
                  }
                </div>
              </section>

              <section class="mt-10">
                <div class="mb-5">
                  <p class="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">Antes do lance</p>
                  <h4 class="text-gray-900 text-2xl font-extrabold mb-2">Recomendações importantes</h4>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  @for (recommendation of recommendations; track recommendation.title) {
                    <article class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                      <div class="mb-3 flex items-center gap-3">
                        <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                          </svg>
                        </div>
                        <h5 class="text-gray-900 font-bold leading-snug">{{ recommendation.title }}</h5>
                      </div>
                      <p class="text-sm text-gray-600 leading-relaxed">{{ recommendation.description }}</p>
                    </article>
                  }
                </div>
              </section>

              <section class="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-5 md:p-6">
                <div class="flex flex-col gap-4 md:flex-row md:items-start">
                  <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-slate-700">
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-sm md:text-base text-slate-700 leading-relaxed">
                      Os dados exibidos possuem caráter informativo e estimado, podendo conter divergências ou desatualizações. A Casato Brasil recomenda validação documental, jurídica e presencial antes de qualquer decisão relacionada ao imóvel.
                    </p>
                    <p class="mt-3 text-xs text-slate-500 leading-relaxed">
                      A plataforma não fornece consultoria jurídica, financeira ou recomendação de investimento.
                    </p>
                  </div>
                </div>
              </section>
              } @else {
                <ng-container *ngTemplateOutlet="guestTabAccessTemplate"></ng-container>
              }
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>

  @if ((isLogged | async) === false) {
    <app-why-choose-template></app-why-choose-template>
    <app-mission-template></app-mission-template>
  }
}

<ng-template #guestTabAccessTemplate>
  <div class="rounded-xl border border-blue-100 bg-blue-50 px-5 py-8 text-center sm:px-8">
    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-blue-700">
      <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2h-1V8a5 5 0 00-10 0v3H6a2 2 0 00-2 2v6a2 2 0 002 2zm3-10V8a3 3 0 116 0v3H9z"/>
      </svg>
    </div>
    <h3 class="mt-4 text-lg font-bold text-gray-900 font-['Inter']">
      Informações disponíveis com login ou assinatura
    </h3>
    <p class="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 font-['Inter']">
      Faça login ou assine para acessar análises, documentos, simulação de custos, entorno e validações completas deste imóvel.
      Você pode testar a Casato por 7 dias durante o trial.
    </p>
    <button type="button"
       class="mt-5 inline-flex items-center justify-center rounded-md bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200"
       (click)="openAuthChoiceModal()">
      Logar ou assinar agora
    </button>
  </div>
</ng-template>

@if (showAuthChoiceModal) {
  <div class="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="auth-choice-title">
    <div class="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-2xl md:p-8">
      <button
        type="button"
        class="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        aria-label="Fechar"
        (click)="closeAuthChoiceModal()">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
      <h4 id="auth-choice-title" class="text-gray-900 text-2xl font-extrabold font-['Inter'] leading-8">
        Como deseja continuar?
      </h4>
      <p class="mt-4 text-gray-500 text-base font-normal font-['Inter'] leading-relaxed">
        Faça login se já tiver conta ou assine para liberar todos os imóveis e análises da Casato. Você pode testar por 7 dias durante o trial.
      </p>
      <div class="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <a routerLink="/login"
           class="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-100">
          Login
        </a>
        <a routerLink="/planos"
           class="inline-flex w-full items-center justify-center rounded-md bg-blue-700 px-5 py-3 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200">
          Assinar agora
        </a>
      </div>
    </div>
  </div>
}

@if (showFavoriteAlertConsentModal) {
  <app-favorite-alert-consent-modal
    [isSaving]="isSavingFavoriteAlertConsent"
    [errorMessage]="favoriteAlertConsentError"
    (accepted)="saveFavoriteAlertConsent(true)"
    (declined)="saveFavoriteAlertConsent(false)">
  </app-favorite-alert-consent-modal>
}
